import { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Send, Paperclip } from 'lucide-react';
import {
  EntityMentionType,
  type LlmChatSlashCommandDto,
  type MentionableRecordDto,
} from '@paladin-monorepo/common';
import { Button } from '@/components/ui/button';
import { LlmChatAttachmentChip } from './LlmChatAttachmentChip';
import { LlmChatMentionPicker } from './LlmChatMentionPicker';
import { LlmChatSlashCommandPicker } from './LlmChatSlashCommandPicker';
import { findSlashCommandQuery, insertSlashCommand } from '@/lib/llm-chat-slash-commands';
import { LLM_CHAT_ATTACHMENT_ACCEPT } from '@/lib/llm-chat-attachment-file-types';
import {
  collapseReferenceToPrefix,
  completeMentionPrefix,
  findMentionTrigger,
  findReferenceToCollapse,
  insertMention,
  realignReferences,
  toContentWithTokens,
  type MentionPrefixes,
  type MentionTrigger,
  type TrackedReference,
} from '@/lib/llm-chat-mentions';

interface LlmChatInputProps {
  onSend: (content: string, files: File[]) => void;
  disabled?: boolean;
  /** Enables `/` command suggestions; they depend on the session's agent. */
  sessionId?: string;
  /** Replaces the composer text when `nonce` changes; the user still sends it. */
  draft?: ChatComposerDraft | null;
}

export interface ChatComposerDraft {
  text: string;
  nonce: number;
}

export function LlmChatInput({ onSend, disabled, sessionId, draft }: LlmChatInputProps) {
  const { t } = useTranslation();
  const [content, setContent] = useState('');
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [trigger, setTrigger] = useState<MentionTrigger | null>(null);
  const [references, setReferences] = useState<TrackedReference[]>([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounterRef = useRef(0);
  const mentionKeyHandlerRef = useRef<((event: KeyboardEvent) => boolean) | null>(null);
  const [slashQuery, setSlashQuery] = useState<string | null>(null);
  const slashKeyHandlerRef = useRef<((event: KeyboardEvent) => boolean) | null>(null);

  const [appliedDraft, setAppliedDraft] = useState(draft);
  if (draft !== appliedDraft) {
    setAppliedDraft(draft);
    if (draft) {
      setContent(draft.text);
      setReferences([]);
    }
  }
  useEffect(() => {
    if (draft) textareaRef.current?.focus();
  }, [draft]);

  const syncSlashQuery = useCallback(
    (value: string, caret: number) => setSlashQuery(sessionId ? findSlashCommandQuery(value, caret) : null),
    [sessionId],
  );

  const mentionPrefixes = useMemo<MentionPrefixes>(
    () => ({
      [EntityMentionType.Inquiry]: t('llmChat.mentions.types.inquiry'),
      [EntityMentionType.Offer]: t('llmChat.mentions.types.offer'),
      [EntityMentionType.Project]: t('llmChat.mentions.types.project'),
      [EntityMentionType.DecisionCase]: t('llmChat.mentions.types.decisionCase'),
      [EntityMentionType.CaseStudy]: t('llmChat.mentions.types.caseStudy'),
    }),
    [t],
  );

  /**
   * Mirrors the live trigger.
   *
   * Clicking a suggestion blurs the textarea before the click completes, and the blur hides
   * the picker. The selection handlers read this ref rather than the state, so the run being
   * replaced is still known by the time the click lands.
   */
  const lastTriggerRef = useRef<MentionTrigger | null>(null);

  const openTrigger = useCallback((next: MentionTrigger | null) => {
    setTrigger(next);
    if (next) lastTriggerRef.current = next;
  }, []);

  /** Hides the picker without forgetting where its run was. */
  const hidePicker = useCallback(() => setTrigger(null), []);

  /** Hides the picker and discards the run - for Escape, insert and submit. */
  const closeTrigger = useCallback(() => {
    setTrigger(null);
    lastTriggerRef.current = null;
  }, []);

  /** Re-reads the `@` run and keeps tracked ranges anchored after any edit. */
  const syncMentionState = useCallback(
    (nextValue: string, caret: number) => {
      setReferences((current) => {
        const aligned = realignReferences(nextValue, current);
        // The trigger needs the realigned ranges, so a `@` inside an inserted reference
        // cannot re-open the picker over itself.
        openTrigger(findMentionTrigger(nextValue, caret, aligned));
        return aligned;
      });
    },
    [openTrigger],
  );

  const applyValue = useCallback(
    (nextValue: string, caret: number) => {
      setContent(nextValue);
      closeTrigger();
      // The caret must land after the browser has taken the new value.
      window.requestAnimationFrame(() => {
        const node = textareaRef.current;
        if (!node) return;
        node.focus();
        node.setSelectionRange(caret, caret);
      });
    },
    [closeTrigger],
  );

  /**
   * Like `applyValue`, but reopens the picker at the new caret instead of closing it.
   *
   * Used after a reference collapses to its prefix: the caret then sits on a completed
   * prefix, which is exactly what makes the picker offer that type's records again.
   */
  const applyCollapse = useCallback(
    (nextValue: string, caret: number, nextReferences: TrackedReference[]) => {
      setContent(nextValue);
      window.requestAnimationFrame(() => {
        const node = textareaRef.current;
        if (!node) return;
        node.focus();
        node.setSelectionRange(caret, caret);
        openTrigger(findMentionTrigger(nextValue, caret, nextReferences));
      });
    },
    [openTrigger],
  );

  const handleSelectRecord = useCallback(
    (record: MentionableRecordDto) => {
      const active = trigger ?? lastTriggerRef.current;
      if (!active) return;
      const result = insertMention(
        content,
        active,
        record,
        mentionPrefixes,
        t('llmChat.mentions.untitled'),
      );
      setReferences((current) => [...realignReferences(result.value, current), result.reference]);
      applyValue(result.value, result.caret);
    },
    [applyValue, content, mentionPrefixes, t, trigger],
  );

  const handleSelectType = useCallback(
    (type: EntityMentionType) => {
      const active = trigger ?? lastTriggerRef.current;
      if (!active) return;
      const result = completeMentionPrefix(content, active, type, mentionPrefixes);
      setContent(result.value);
      window.requestAnimationFrame(() => {
        const node = textareaRef.current;
        if (!node) return;
        node.focus();
        node.setSelectionRange(result.caret, result.caret);
        openTrigger(findMentionTrigger(result.value, result.caret, references));
      });
    },
    [content, mentionPrefixes, openTrigger, references, trigger],
  );

  const handleSelectSlashCommand = useCallback(
    (command: LlmChatSlashCommandDto) => {
      const result = insertSlashCommand(content, command.name);
      setSlashQuery(null);
      applyValue(result.value, result.caret);
    },
    [applyValue, content],
  );

  const addFiles = useCallback((files: File[]) => {
    if (files.length === 0) return;
    setPendingFiles((prev) => [...prev, ...files]);
  }, []);

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      const items = Array.from(e.clipboardData.items);
      const pastedFiles = items
        .filter((item) => item.kind === 'file')
        .map((item) => item.getAsFile())
        .filter((file): file is File => file != null);
      if (pastedFiles.length === 0) return;
      e.preventDefault();
      addFiles(pastedFiles);
    },
    [addFiles],
  );

  const handleSubmit = useCallback(() => {
    const trimmed = content.trim();
    if (!trimmed && pendingFiles.length === 0) return;

    // Readable `@Type: name` runs become `[[ref:...]]` tokens only at this point.
    onSend(toContentWithTokens(trimmed, references), pendingFiles);
    setContent('');
    setPendingFiles([]);
    setReferences([]);
    closeTrigger();
    setSlashQuery(null);
  }, [closeTrigger, content, pendingFiles, onSend, references]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (trigger && mentionKeyHandlerRef.current?.(e.nativeEvent as KeyboardEvent)) {
      e.preventDefault();
      return;
    }
    if (slashQuery !== null && slashKeyHandlerRef.current?.(e.nativeEvent as KeyboardEvent)) {
      e.preventDefault();
      return;
    }

    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSubmit();
      return;
    }

    // Deleting any part of a reference takes the whole caption and leaves `@Prefix: `, so
    // the picker reopens on that type and a different record can be chosen straight away.
    if (e.key === 'Backspace' || e.key === 'Delete') {
      const node = e.currentTarget as HTMLTextAreaElement;
      const target = findReferenceToCollapse(
        references,
        node.selectionStart,
        node.selectionEnd,
        e.key === 'Backspace' ? 'backward' : 'forward',
      );
      if (target) {
        e.preventDefault();
        const result = collapseReferenceToPrefix(content, references, target);
        setReferences(result.references);
        applyCollapse(result.value, result.caret, result.references);
      }
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    addFiles(Array.from(e.target.files || []));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current += 1;
    if (e.dataTransfer.types.includes('Files')) {
      setIsDragging(true);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    addFiles(files);
  };

  const removeFile = (index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <div
      className="shrink-0 border-t bg-background p-4"
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div className="mx-auto max-w-3xl">
        {isDragging && (
          <div className="mb-2 flex items-center justify-center rounded-lg border-2 border-dashed border-primary/50 bg-primary/5 px-4 py-6 text-sm text-primary">
            {t('llmChat.dropFilesHere')}
          </div>
        )}

        {pendingFiles.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-2">
            {pendingFiles.map((file, i) => (
              <LlmChatAttachmentChip
                key={i}
                name={file.name}
                size={file.size}
                onRemove={() => removeFile(i)}
              />
            ))}
          </div>
        )}

        <div className="relative flex items-end gap-2">
          {trigger && (
            <LlmChatMentionPicker
              query={trigger.query}
              onSelectRecord={handleSelectRecord}
              onSelectType={handleSelectType}
              onClose={closeTrigger}
              registerKeyHandler={(handler) => {
                mentionKeyHandlerRef.current = handler;
              }}
            />
          )}
          {sessionId && slashQuery !== null && (
            <LlmChatSlashCommandPicker
              sessionId={sessionId}
              query={slashQuery}
              onSelect={handleSelectSlashCommand}
              onClose={() => setSlashQuery(null)}
              registerKeyHandler={(handler) => {
                slashKeyHandlerRef.current = handler;
              }}
            />
          )}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={LLM_CHAT_ATTACHMENT_ACCEPT}
            className="hidden"
            onChange={handleFileSelect}
          />
          <Button
            variant="ghost"
            size="icon"
            className="shrink-0"
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled}
          >
            <Paperclip className="h-4 w-4" />
          </Button>

          <textarea
            ref={textareaRef}
            value={content}
            onChange={(e) => {
              setContent(e.target.value);
              syncMentionState(e.target.value, e.target.selectionStart ?? e.target.value.length);
              syncSlashQuery(e.target.value, e.target.selectionStart ?? e.target.value.length);
            }}
            onSelect={(e) => {
              const node = e.currentTarget;
              openTrigger(findMentionTrigger(node.value, node.selectionStart ?? 0, references));
            }}
            onBlur={() => {
              hidePicker();
              setSlashQuery(null);
            }}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            placeholder={t('llmChat.inputPlaceholder')}
            disabled={disabled}
            rows={1}
            className="max-h-32 min-h-[40px] flex-1 resize-none rounded-lg border bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            style={{ height: 'auto', overflow: 'hidden' }}
            onInput={(e) => {
              const target = e.target as HTMLTextAreaElement;
              target.style.height = 'auto';
              target.style.height = `${Math.min(target.scrollHeight, 128)}px`;
            }}
          />

          <Button
            size="icon"
            className="shrink-0"
            onClick={handleSubmit}
            disabled={disabled || (!content.trim() && pendingFiles.length === 0)}
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>

        <div className="mt-1.5 text-center text-[11px] text-muted-foreground">
          <p>{t('llmChat.aiDisclaimer')}</p>
          <p>{t('llmChat.sendHint')}</p>
        </div>
      </div>
    </div>
  );
}
